import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import api from "@/lib/api";
import { Loader2, CheckCircle2, AlertTriangle, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";

export default function ShareAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const { data: info, isLoading, error } = useQuery({
    queryKey: ["share-info", token],
    queryFn: async () => (await api.get(`/share/${token}/info`)).data,
    enabled: !!user,
    retry: false,
  });

  const accept = useMutation({
    mutationFn: async () => (await api.post(`/share/${token}/accept`)).data,
    onSuccess: (data) => {
      toast.success("Te has unido al board");
      navigate(`/boards/${data.board_id}`, { replace: true });
    },
    onError: () => toast.error("No se pudo aceptar la invitación"),
  });

  useEffect(() => {
    if (!authLoading && !user) {
      // Persist intent so login can redirect back here.
      sessionStorage.setItem("post_login_redirect", `/share/${token}`);
      navigate("/login", { replace: true });
    }
  }, [user, authLoading, token, navigate]);

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen grid place-items-center relative z-10 p-6">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center relative z-10 p-6" data-testid="share-accept-error">
        <div className="glass-panel rounded-3xl p-10 max-w-md text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
          <h2 className="font-display text-2xl font-semibold mt-4">Enlace inválido</h2>
          <p className="text-sm text-muted-foreground mt-2">Pídele a quien te invitó que comparta el enlace nuevamente.</p>
          <Button onClick={() => navigate("/")} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white">Ir al dashboard</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center relative z-10 p-6" data-testid="share-accept">
      <div className="glass-panel rounded-3xl p-10 max-w-md w-full text-center">
        <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-500 to-sky-500 grid place-items-center mx-auto shadow-lg shadow-blue-500/30">
          <Users className="h-7 w-7 text-white" />
        </div>
        <h2 className="font-display text-2xl font-semibold mt-5">{info?.owner?.name || "Alguien"} te invitó</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Unirte al board <strong>«{info?.board?.name}»</strong> como editor.
        </p>
        <div className="mt-6 flex gap-2">
          <Button variant="ghost" onClick={() => navigate("/")} data-testid="share-accept-cancel">Cancelar</Button>
          <Button
            onClick={() => accept.mutate()}
            disabled={accept.isPending}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="share-accept-confirm"
          >
            {accept.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Unirme al board</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
